// =============================================================================
// FILE: ExecutionCard.js
// PPE → MES → Running Execution Card
// UPDATED: 2026-02-26
// ADDITIONS:
//   ✔ Live Planned / Produced / Remaining
//   ✔ Disable LOG OUTPUT when balance = 0
//   ✔ Toast validation messages
//   ✔ Safe MES live updates
// =============================================================================

import React, { useEffect, useState } from "react";
import {
  completeExecution,
  holdExecution,
  logOutput,
} from "../services/mesExecutionService";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// -----------------------------------------------------------------------------
// Delay Intelligence Helper
// -----------------------------------------------------------------------------
const getDelayStatus = (execution) => {
  if (!execution.actual_start_datetime || !execution.planned_end_datetime)
    return "NORMAL";

  const now = new Date();
  const plannedEnd = new Date(execution.planned_end_datetime);

  if (now > plannedEnd) return "DELAYED";

  const diffMinutes = (plannedEnd - now) / 60000;
  if (diffMinutes < 10) return "RISK";

  return "NORMAL";
};

const ExecutionCard = ({ execution, onRefresh }) => {
  const [elapsed, setElapsed] = useState("00:00:00");
  const [goodQty, setGoodQty] = useState("");
  const [scrapQty, setScrapQty] = useState("");

  // ✅ NEW — MES LIVE STATS (safe defaults)
  const [mesStats, setMesStats] = useState({
    planned: execution.planned_qty || 0,
    produced: execution.produced_qty || 0,
    remaining:
      execution.remaining_qty ??
      execution.planned_qty ??
      0,
  });

  // ---------------------------------------------------------------------------
  // Sync stats when execution refreshes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setMesStats({
      planned: execution.planned_qty || 0,
      produced: execution.produced_qty || 0,
      remaining:
        execution.remaining_qty  ??
        execution.planned_qty ??
        0,
    });
  }, [execution]);

  // ---------------------------------------------------------------------------
  // Live elapsed timer
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const calcElapsed = () => {
      if (!execution.actual_start_datetime) return;

      const start = new Date(execution.actual_start_datetime);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);

      const h = String(Math.floor(diff / 3600)).padStart(2, "0");
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
      const s = String(diff % 60).padStart(2, "0");

      setElapsed(`${h}:${m}:${s}`);
    };

    calcElapsed();
    const timer = setInterval(calcElapsed, 1000);
    return () => clearInterval(timer);
  }, [execution]);

  const delayStatus = getDelayStatus(execution);

  const statusConfig = {
    NORMAL: {
      color: "#15803d",
      label: "🟢 ON TIME",
      gradient: "linear-gradient(135deg,#374151,#1f2937)",
    },
    RISK: {
      color: "#f59e0b",
      label: "🟡 AT RISK",
      gradient: "linear-gradient(135deg,#f59e0b,#fbbf24)",
    },
    DELAYED: {
      color: "#dc2626",
      label: "🔴 DELAYED",
      gradient: "linear-gradient(135deg,#dc2626,#ef4444)",
    },
  };

  const status = statusConfig[delayStatus];

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const handleComplete = async () => {
    await completeExecution(execution.id);
    onRefresh?.();
  };

  const handleHold = async () => {
    await holdExecution(execution.id, "MANUAL_HOLD");
    onRefresh?.();
  };

  // ✅ UPDATED — SAFE OUTPUT LOGGING
  const handleLogOutput = async () => {
    try {
      const res = await logOutput(
        execution.id,
        Number(goodQty || 0),
        Number(scrapQty || 0)
      );

      // ✅ IMPORTANT: axios interceptor returns 400 as normal response
      if (res?.success === false) {
        toast.error(res.message || "Validation failed");
        return;
      }

      // ✅ success flow
      if (res?.data) {
        setMesStats((prev) => ({
          planned: res.data.planned_qty ?? prev.planned,
          produced: res.data.produced_qty ?? prev.produced,
          remaining: res.data.remaining_qty ?? res.data.balance_remaining,
        }));
      }

      toast.success("Output logged successfully");

      setGoodQty("");
      setScrapQty("");

      onRefresh?.();

    } catch (err) {
      console.error("MES Output Error:", err);
      toast.error(err.message || "Failed to log output");
    }
  };

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{
        borderRadius: 16,
        overflow: "hidden",
        background: "#ffffff",
        boxShadow: "0 10px 22px rgba(0,0,0,0.12)",
        border: `2px solid ${status.color}`,
      }}
    >
      {/* ================= HEADER ================= */}
      <div
        style={{
          background: status.gradient,
          color: "#fff",
          padding: "12px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <strong>⚙ Execution #{execution.id}</strong>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#fff",
              animation: "pulse 1.4s infinite",
            }}
          />
          {status.label}
        </div>
      </div>

      {/* ================= BODY ================= */}
      <div style={{ padding: 16 }}>

        {/* ⭐ MES CONTEXT BLOCK */}
        <div
          style={{
            background: "#f8fafc",
            borderRadius: 10,
            padding: 12,
            marginBottom: 12,
            fontSize: 14,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
            gap: 6,
          }}
        >
          <div><strong>WO:</strong> {execution.work_order_no || "-"}</div>
          <div><strong>Plan:</strong> {execution.plan_no || "-"}</div>
          <div><strong>Item:</strong> {execution.item_code} - {execution.item_name}</div>
          <div><strong>Qty:</strong> {execution.planned_qty || "-"}</div>
          <div><strong>Operation:</strong> {execution.operation_name}</div>
          <div><strong>Workcenter:</strong> {execution.workcenter}</div>
        </div>

        <p>⏱ Elapsed: <strong>{elapsed}</strong></p>

        {/* ✅ NEW — LIVE MES STATUS */}
        <div
          style={{
            background: "#ecfeff",
            borderRadius: 10,
            padding: 12,
            marginBottom: 12,
            fontSize: 14,
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            fontWeight: 600,
          }}
        >
          <div>📦 Planned : {mesStats.planned}</div>
          <div>✅ Produced : {mesStats.produced}</div>
          <div>🟢 Remaining : {mesStats.remaining}</div>
        </div>

        {/* OUTPUT */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            placeholder="Good Qty"
            value={goodQty}
            onChange={(e) => setGoodQty(e.target.value)}
            style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
          />

          <input
            placeholder="Scrap Qty"
            value={scrapQty}
            onChange={(e) => setScrapQty(e.target.value)}
            style={{ padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
          />

          <button
            onClick={handleLogOutput}
            disabled={mesStats.remaining <= 0}
            style={{
              background:
                mesStats.remaining <= 0
                  ? "#9ca3af"
                  : "linear-gradient(135deg,#2563eb,#3b82f6)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              fontWeight: 600,
              cursor:
                mesStats.remaining <= 0 ? "not-allowed" : "pointer",
            }}
          >
            LOG OUTPUT
          </button>
        </div>

        {/* ACTIONS */}
        <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap:"wrap" }}>
          <button
            onClick={handleHold}
            style={{
              background: "linear-gradient(135deg,#f59e0b,#fbbf24)",
              border: "none",
              borderRadius: 10,
              padding: "10px 18px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            HOLD
          </button>

          <button
            onClick={handleComplete}
            style={{
              background: "linear-gradient(135deg,#dc2626,#ef4444)",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: "10px 18px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            COMPLETE
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.8); opacity: 0.6; }
          50% { transform: scale(1.4); opacity: 1; }
          100% { transform: scale(0.8); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

export default ExecutionCard;